import { useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import type { Round } from '@/components/reviews/types'

/** Stream every queue page, discarding stale responses when the filters change. */
export function useReviewQueue(kind: string, program: string, mine: boolean, enabled = true, reviewerScope = '') {
  const generation = useRef(0)
  const [rows, setRows] = useState<Round[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(true)
  async function load(after?: string) {
    if (!enabled) return
    const current = ++generation.current
    setBusy(true)
    setError('')
    try {
      let next = after
      let accumulated: Round[] = []
      const seen = new Set(after ? [after] : [])
      do {
        const result = await api.get<{ reviews: Round[]; cursor: string | null }>('/sme/reviews', {
          kind, mine: mine ? '1' : '0', ...(program ? { program } : {}), ...(next ? { cursor: next } : {}),
        })
        if (current !== generation.current) return
        accumulated = [...accumulated, ...result.reviews]
        next = result.cursor ?? undefined
        setCursor(result.cursor)
        setRows(previous => Array.from(new Map([...(after ? previous : []), ...accumulated]
          .map(row => [row.round_id, row])).values()))
        if (next && seen.has(next)) throw new Error('The review list could not finish loading. Please retry.')
        if (next) seen.add(next)
      } while (next && current === generation.current)
    } catch (err) {
      if (current === generation.current) setError((err as Error).message)
    } finally {
      if (current === generation.current) setBusy(false)
    }
  }
  useEffect(() => {
    setRows([])
    setCursor(null)
    if (!enabled) {
      setBusy(false)
      return
    }
    void load()
    return () => {
      generation.current++
    }
  }, [kind, program, mine, enabled, reviewerScope])
  return { rows, cursor, error, busy, load }
}
